import React, { useState } from "react";
import {
  Button,
  Col,
  Divider,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Tag,
  Typography
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import PageToolbar from "../../../design-system/PageToolbar";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { createSupplier, deleteSupplier, openShopSession, postToShop, updateSupplier } from "./api";
import {
  bpToPercent,
  decimalInputProps,
  formatDateTime,
  formatPercent,
  percentToBp
} from "./format";

const EMPTY = {
  name: "",
  customerNumber: "",
  accountingSupplier: null,
  defaultDiscount: 0,
  discountGroups: [],
  ids: { enabled: false, url: "", version: "2.5", customerNumber: "", username: "", password: "" }
};

/** Opens the supplier's web shop through IDS Connect (the basket comes back to the app). */
export const openShop = async (supplierId, action = "WKE", extra = {}) => {
  const res = await openShopSession({ supplier: supplierId, action, ...extra });
  postToShop(res.data.data.form);
};

const SupplierModal = ({ meta, supplier, onClose, onSaved }) => {
  const { t, i18n } = useTranslation("dashboard");
  const numbers = decimalInputProps(i18n.language);
  const [form, setForm] = useState(() =>
    supplier?._id
      ? { ...EMPTY, ...supplier, ids: { ...EMPTY.ids, ...supplier.ids, password: "" } }
      : EMPTY
  );
  const [busy, setBusy] = useState(false);
  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const setIds = (key, value) => setForm(prev => ({ ...prev, ids: { ...prev.ids, [key]: value } }));
  const setGroup = (index, key, value) =>
    set(
      "discountGroups",
      form.discountGroups.map((g, i) => (i === index ? { ...g, [key]: value } : g))
    );

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        name: form.name,
        customerNumber: form.customerNumber,
        accountingSupplier: form.accountingSupplier || null,
        defaultDiscount: form.defaultDiscount,
        discountGroups: form.discountGroups.map(g => ({
          code: g.code,
          name: g.name,
          discount: g.discount
        })),
        ids: { ...form.ids, password: form.ids.password || undefined }
      };
      if (supplier?._id) await updateSupplier(supplier._id, body);
      else await createSupplier(body);
      Success(t("Supplier saved"));
      onSaved();
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      rootClassName="materials-page"
      open
      width={760}
      title={supplier?._id ? supplier.name : t("New supplier")}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t("Cancel")}
        </Button>,
        <Button key="save" type="primary" loading={busy} onClick={save}>
          {t("Save")}
        </Button>
      ]}
    >
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <div className="mat-muted">{t("Name")}</div>
          <Input value={form.name} onChange={e => set("name", e.target.value)} />
        </Col>
        <Col xs={24} md={12}>
          <div className="mat-muted">{t("Customer number")}</div>
          <Input
            value={form.customerNumber}
            onChange={e => set("customerNumber", e.target.value)}
          />
        </Col>
        {meta.accountingSuppliers.length ? (
          <Col xs={24} md={12}>
            <div className="mat-muted">{t("Supplier in bookkeeping")}</div>
            <Select
              allowClear
              className="mat-full"
              value={form.accountingSupplier}
              onChange={value => set("accountingSupplier", value || null)}
              options={meta.accountingSuppliers.map(s => ({
                value: s._id,
                label: `${s.supplierNumber} ${s.name}`
              }))}
            />
          </Col>
        ) : null}
        <Col xs={24} md={12}>
          <div className="mat-muted">{t("General discount (%)")}</div>
          <InputNumber
            {...numbers}
            className="mat-full"
            min={-1000}
            max={100}
            value={bpToPercent(form.defaultDiscount)}
            onChange={value => set("defaultDiscount", percentToBp(value))}
          />
        </Col>
      </Row>

      <Divider orientation="left">{t("Discount groups")}</Divider>
      <Typography.Paragraph className="mat-muted">
        {t(
          "Discount on the list price per discount group, from your conditions sheet or the supplier's .RAB file."
        )}
      </Typography.Paragraph>
      {form.discountGroups.map((g, index) => (
        <Space key={index} wrap className="mat-full mat-section">
          <Input
            placeholder={t("Code")}
            value={g.code}
            onChange={e => setGroup(index, "code", e.target.value)}
          />
          <Input
            placeholder={t("Name")}
            value={g.name}
            onChange={e => setGroup(index, "name", e.target.value)}
          />
          <InputNumber
            {...numbers}
            min={-1000}
            max={100}
            addonAfter="%"
            value={bpToPercent(g.discount)}
            onChange={value => setGroup(index, "discount", percentToBp(value))}
          />
          {g.source === "import" ? <Tag>{t("from import")}</Tag> : null}
          <Button
            type="link"
            danger
            onClick={() =>
              set(
                "discountGroups",
                form.discountGroups.filter((_, i) => i !== index)
              )
            }
          >
            {t("Remove")}
          </Button>
        </Space>
      ))}
      <Button
        type="dashed"
        block
        className="mat-section"
        onClick={() =>
          set("discountGroups", [...form.discountGroups, { code: "", name: "", discount: 0 }])
        }
      >
        + {t("Add discount group")}
      </Button>

      <Divider orientation="left">{t("IDS Connect shop access")}</Divider>
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Space>
            <Switch checked={form.ids.enabled} onChange={value => setIds("enabled", value)} />
            {t("Open the wholesaler's shop from the app and take the basket back")}
          </Space>
        </Col>
        <Col xs={24} md={16}>
          <div className="mat-muted">{t("Shop address (IDS Connect URL)")}</div>
          <Input
            placeholder="https://"
            inputMode="url"
            value={form.ids.url}
            onChange={e => setIds("url", e.target.value)}
          />
        </Col>
        <Col xs={24} md={8}>
          <div className="mat-muted">{t("Version")}</div>
          <Select
            className="mat-full"
            value={form.ids.version}
            onChange={value => setIds("version", value)}
            options={["2.0", "2.1", "2.2", "2.3", "2.4", "2.5"].map(v => ({ value: v, label: v }))}
          />
        </Col>
        <Col xs={24} md={8}>
          <div className="mat-muted">{t("Customer number")}</div>
          <Input
            value={form.ids.customerNumber}
            onChange={e => setIds("customerNumber", e.target.value)}
          />
        </Col>
        <Col xs={24} md={8}>
          <div className="mat-muted">{t("User")}</div>
          <Input
            value={form.ids.username}
            onChange={e => setIds("username", e.target.value)}
            autoComplete="off"
          />
        </Col>
        <Col xs={24} md={8}>
          <div className="mat-muted">{t("Password")}</div>
          <Input.Password
            autoComplete="new-password"
            placeholder={supplier?.ids?.hasPassword ? t("Stored (leave empty to keep)") : ""}
            value={form.ids.password}
            onChange={e => setIds("password", e.target.value)}
          />
        </Col>
      </Row>
    </Modal>
  );
};

/** Suppliers (wholesalers, manufacturers) with discounts and IDS shop access. */
const SuppliersTab = ({ meta, onChanged }) => {
  const { t, i18n } = useTranslation("dashboard");
  const [editing, setEditing] = useState(null);
  const canEdit = meta.permissions.update;

  const remove = async supplier => {
    try {
      await deleteSupplier(supplier._id);
      onChanged();
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  const columns = [
    { title: t("Name"), dataIndex: "name", key: "name", mobile: "title" },
    {
      title: t("Articles"),
      key: "articles",
      align: "right",
      render: (_, s) => <span className="mat-number">{s.articleCount || 0}</span>
    },
    {
      title: t("Discounts"),
      key: "discounts",
      render: (_, s) =>
        `${formatPercent(s.defaultDiscount, i18n.language)} · ${t("{{count}} groups", { count: s.discountGroups?.length || 0 })}`
    },
    {
      title: t("Last import"),
      key: "lastImportAt",
      render: (_, s) => (s.lastImportAt ? formatDateTime(s.lastImportAt, i18n.language) : "–")
    },
    {
      title: t("Actions"),
      key: "actions",
      render: (_, s) => (
        <Space wrap>
          {s.ids?.enabled && meta.permissions.create ? (
            <Button size="small" onClick={() => openShop(s._id).catch(generalErrorAlert)}>
              {t("Open shop")}
            </Button>
          ) : null}
          {canEdit ? (
            <Button size="small" onClick={() => setEditing(s)}>
              {t("Edit")}
            </Button>
          ) : null}
          {meta.permissions.delete ? (
            <Popconfirm title={t("Delete this supplier?")} onConfirm={() => remove(s)}>
              <Button size="small" danger>
                {t("Delete")}
              </Button>
            </Popconfirm>
          ) : null}
        </Space>
      )
    }
  ];

  return (
    <div>
      <PageToolbar
        actions={
          canEdit ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing({})}>
              {t("New supplier")}
            </Button>
          ) : null
        }
      />
      <ResponsiveTable
        rowKey="_id"
        columns={columns}
        dataSource={meta.suppliers}
        pagination={false}
        mobileAvatar={false}
      />
      {editing ? (
        <SupplierModal
          meta={meta}
          supplier={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            onChanged();
          }}
        />
      ) : null}
    </div>
  );
};

export default SuppliersTab;
